import React, { useMemo, useState } from 'react';
import { CheckCheck, Image, RefreshCw, Search } from 'lucide-react';

export default function DetectionLog({
  detections,
  onViewEvidence,
  onResolve,
  onRefresh,
  canResolve = true,
  loading = false,
  emptyMessage = 'No detections found for selected date.'
}) {
  const [query, setQuery] = useState('');
  const formatSeen = (value, fallback) => value || fallback || '-';

  // Matches on Product ID, camera, or date - the three things a user would
  // actually know when hunting for a specific past alert.
  const filteredDetections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return detections;
    return detections.filter((row) => (
      String(row.product_id || '').toLowerCase().includes(q)
      || String(row.camera || '').toLowerCase().includes(q)
      || String(row.date || '').toLowerCase().includes(q)
    ));
  }, [detections, query]);

  return (
    <section className="data-card">
      <div className="data-card__header">
        <h3>Detection Log</h3>
        <div className="data-card__header-actions">
          <div className="log-search">
            <Search size={14} />
            <input
              type="text"
              placeholder="Search ID, CAM, or Date…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <button className="ghost-btn" type="button" onClick={onRefresh}>
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Time</th>
              <th>Camera</th>
              <th>Class</th>
              <th>Product ID</th>
              <th>Batch</th>
              <th>Confidence</th>
              <th>First Seen</th>
              <th>Last Seen</th>
              <th>Hits</th>
              <th>Duration</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {!loading && filteredDetections.length === 0 && (
              <tr>
                <td colSpan={12} className="empty-cell">
                  {detections.length === 0
                    ? emptyMessage
                    : 'No detections match your search.'}
                </td>
              </tr>
            )}
            {filteredDetections.map((row) => (
              <tr key={row.id}>
                <td>{row.timestamp}</td>
                <td>{row.camera}</td>
                <td>{row.class_name}</td>
                <td>{row.product_id || '-'}</td>
                <td>{row.batch_id || '-'}</td>
                <td>{Number(row.confidence).toFixed(1)}%</td>
                <td>{formatSeen(row.first_seen_at, row.first_seen || row.timestamp)}</td>
                <td>{formatSeen(row.last_seen_at, row.last_seen || row.timestamp)}</td>
                <td>{row.occurrences || 1}</td>
                <td>{`${Math.round(Number(row.duration_sec || 0))}s`}</td>
                <td>
                  {row.resolved ? (
                    <span className="pill pill--ok">Resolved</span>
                  ) : (
                    <span className="pill pill--warn">Open</span>
                  )}
                </td>
                <td className="action-td">
                  <button
                    className="icon-btn"
                    type="button"
                    onClick={() => onViewEvidence(row.id)}
                    title="View Evidence"
                  >
                    <Image size={14} />
                  </button>
                  {canResolve && !row.resolved && (
                    <button
                      className="icon-btn"
                      type="button"
                      onClick={() => onResolve(row.id)}
                      title="Mark Resolved"
                    >
                      <CheckCheck size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
